import { Group, Tooltip, Popover, Button, Stack } from "@mantine/core"
import { useState, useCallback } from "react"

import { BUSINESS_METRIC_NAME } from "@costory/shared/utils/charts"

import { EVENTS_NAME } from "@costory/front/components/charts/ChartTooltip"
import { LegendEntry } from "@costory/front/components/charts/useLegendHover"

interface SmartLegendProps {
  payload: LegendEntry[]
  onEntryClick?: (entry: LegendEntry) => void
  showComparisonWithLegend?: string
  hideMetricLine?: boolean
  hideComparison?: boolean
  hiddenSeries?: Set<string>
  onHoverChange?: (hoveredEntry: LegendEntry | null) => void
  maxVisibleLegends?: number
}

const Dot = ({ color, opacity = 1 }: { color: string; opacity?: number }) => (
  <span
    style={{
      display: "inline-block",
      width: 12,
      height: 12,
      background: color,
      borderRadius: "50%",
      marginRight: 6,
      flexShrink: 0,
      opacity,
    }}
  />
)

const getDotColor = (
  { value, color, dataKey }: LegendEntry,
  hideMetricLine: boolean | undefined,
  hideComparison: boolean | undefined,
  hiddenSeries?: Set<string>,
): string => {
  if (hideMetricLine && value === BUSINESS_METRIC_NAME) {
    return "var(--mantine-color-borderSubtle-0)"
  }
  if (hideComparison && value.endsWith("(previous)")) {
    return "var(--mantine-color-borderSubtle-0)"
  }
  if (hiddenSeries && hiddenSeries.has(dataKey)) {
    return "var(--mantine-color-borderSubtle-0)"
  }
  return color
}

interface LegendLabelProps {
  entry: LegendEntry
  onClick?: () => void
  onHover?: (entry: LegendEntry | null) => void
  isTruncated?: boolean
  showComparisonWithLegend?: string
  hideMetricLine?: boolean
  hideComparison?: boolean
  withTooltip?: boolean
  opacity?: number
  hiddenSeries?: Set<string>
}

const LegendLabel = ({
  entry,
  onClick,
  onHover,
  isTruncated,
  showComparisonWithLegend,
  hideMetricLine,
  hideComparison,
  withTooltip = true,
  opacity = 1,
  hiddenSeries,
}: LegendLabelProps) => {
  const { value } = entry

  const resolvedLabel = showComparisonWithLegend
    ? value.endsWith("(previous)")
      ? `${showComparisonWithLegend} (previous)`
      : showComparisonWithLegend
    : value

  const displayLabel =
    isTruncated && resolvedLabel.length > 20
      ? resolvedLabel.slice(0, 17) + "..."
      : resolvedLabel

  const labelContent = (
    <span
      style={{
        padding: "2px 3px",
        fontSize: 14,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        opacity,
      }}
      onClick={onClick}
      onMouseEnter={() => onHover?.(entry)}
      onMouseLeave={() => onHover?.(null)}
    >
      <Dot
        color={getDotColor(entry, hideMetricLine, hideComparison, hiddenSeries)}
        opacity={opacity}
      />
      {displayLabel}
    </span>
  )

  return withTooltip ? (
    <Tooltip label={resolvedLabel}>{labelContent}</Tooltip>
  ) : (
    labelContent
  )
}

export const SmartLegend = ({
  payload = [],
  onEntryClick,
  showComparisonWithLegend,
  hideMetricLine,
  hideComparison,
  hiddenSeries,
  onHoverChange,
  maxVisibleLegends = 4,
}: SmartLegendProps) => {
  const [popoverOpened, setPopoverOpened] = useState(false)
  const [hoveredEntry, setHoveredEntry] = useState<LegendEntry | null>(null)

  const legendItems = payload.filter(({ dataKey }) => dataKey !== EVENTS_NAME)

  const metricLegend = legendItems.find(
    ({ value }) => value === BUSINESS_METRIC_NAME,
  )
  const otherItems = legendItems.filter(
    ({ value }) => value !== BUSINESS_METRIC_NAME,
  )

  const visible = [
    ...(metricLegend ? [metricLegend] : []),
    ...otherItems.slice(0, metricLegend ? 3 : maxVisibleLegends),
  ]

  const visibleSet = new Set(visible.map(({ value }) => value))
  const hidden = legendItems.filter(({ value }) => !visibleSet.has(value))

  const handleHover = useCallback(
    (entry: LegendEntry | null) => {
      setHoveredEntry(entry)
      onHoverChange?.(entry)
    },
    [onHoverChange],
  )

  const handleClick = useCallback(
    (entry: LegendEntry) => {
      onEntryClick?.(entry)
    },
    [onEntryClick],
  )

  const getOpacity = (entry: LegendEntry) => {
    if (!hoveredEntry) return 1

    const isHovered = hoveredEntry.value === entry.value
    return isHovered ? 1 : 0.5
  }

  const renderLabel = (entry: LegendEntry, idx: number, isHidden: boolean) => (
    <LegendLabel
      key={`${entry.value}-${isHidden ? "hidden" : "visible"}-${idx}`}
      entry={entry}
      isTruncated={!isHidden}
      withTooltip={!isHidden}
      onClick={() => handleClick(entry)}
      onHover={handleHover}
      showComparisonWithLegend={showComparisonWithLegend}
      hideMetricLine={hideMetricLine}
      hideComparison={hideComparison}
      hiddenSeries={hiddenSeries}
      opacity={getOpacity(entry)}
    />
  )

  return (
    <Group wrap="nowrap">
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: "7px",
        }}
      >
        {visible.map((entry, idx) => renderLabel(entry, idx, false))}
      </div>

      {hidden.length > 0 && (
        <Popover
          opened={popoverOpened}
          onChange={setPopoverOpened}
          position="top"
          withinPortal
        >
          <Popover.Target>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setPopoverOpened((o) => !o)}
              style={{ minWidth: 36 }}
            >
              +{hidden.length} more
            </Button>
          </Popover.Target>
          <Popover.Dropdown style={{ minWidth: 300, maxWidth: 600 }}>
            <Stack gap={4}>
              {hidden.map((entry, idx) => renderLabel(entry, idx, true))}
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}
    </Group>
  )
}
